import {
  KgCo2ePerM2GfaByBuildingType,
  ScopeTarget,
  TargetBuildingType,
} from '@features/lifecycles/scope-presets.data';
import { Lifecycle } from '@shared/models/lifecycles.model';
import {
  ActivityMix,
  ActivityType,
  EnsureActivityMap,
} from '@shared/models/design/activities/activity-type.model';

/**
 * The byggnadstyp each program activity is graded as. Retail and industry have
 * no row in either manual and fall to "Övriga byggnadstyper", which is what both
 * prescribe for building types they do not list.
 *
 * `null` means the activity is not a byggnadstyp at all: stairwells, storage,
 * utilities, inner walls, facades, installations and garage are shared building
 * fabric — a garage under a block of flats is part of the flerbostadshus BTA,
 * not a separate certification type. Weighting by the graded activities alone
 * spreads their area across the building types in proportion, which is exactly
 * that reading.
 *
 * Every activity is listed, so adding one to {@link ActivityType} fails the
 * build here until someone decides what it is graded as.
 */
const TARGET_BUILDING_TYPE_BY_ACTIVITY: EnsureActivityMap<
  Record<ActivityType, TargetBuildingType | null>
> = {
  [ActivityType.Residential]: TargetBuildingType.MultiDwelling,
  [ActivityType.Office]: TargetBuildingType.Office,
  [ActivityType.School]: TargetBuildingType.School,
  [ActivityType.Retail]: TargetBuildingType.Other,
  [ActivityType.Industry]: TargetBuildingType.Other,

  [ActivityType.Facades]: null,
  [ActivityType.Garage]: null,
  [ActivityType.Innerwalls]: null,
  [ActivityType.Installations]: null,
  [ActivityType.Other]: null,
  [ActivityType.Stairwells]: null,
  [ActivityType.Storage]: null,
  [ActivityType.Unset]: null,
  [ActivityType.Utilities]: null,
};

/** The byggnadstypes at least one activity is graded as. */
export const GRADED_BUILDING_TYPES = Object.values(
  TARGET_BUILDING_TYPE_BY_ACTIVITY,
).filter((type): type is TargetBuildingType => type !== null);

/**
 * The limit values a target grades against. A `reduction` target has none of its
 * own — it cuts the scope's reference values, so it resolves to nothing when the
 * scope has none.
 */
export const getTargetValuesByBuildingType = (
  target: ScopeTarget,
  referenceValues?: KgCo2ePerM2GfaByBuildingType,
): KgCo2ePerM2GfaByBuildingType | undefined => {
  if (target.kind === 'absolute') return target.kgCo2ePerM2Gfa;
  if (!referenceValues) return undefined;
  return Object.entries(referenceValues).reduce(
    (values, [type, reference]) => ({
      ...values,
      [type]: reference * (1 - target.reduction),
    }),
    {} as KgCo2ePerM2GfaByBuildingType,
  );
};

/**
 * The limit value for a building of several types, weighted by the area of each
 * (Miljöbyggnad 4.1 tabell 4.1, BREEAM-SE v6 Mat 01 ref 2.1 — both weight their
 * values on BTA per byggnadstyp).
 *
 * A project whose activities establish no byggnadstyp is graded as "Övriga
 * byggnadstyper", the catch-all row both systems publish — Miljöbyggnad as its
 * own row, BREEAM as the kontor value its table's footnote extends to every
 * other building. Undefined only when the target has no values at all.
 */
export const getWeightedTargetValue = (
  target: ScopeTarget,
  activityMix: ActivityMix,
  referenceValues?: KgCo2ePerM2GfaByBuildingType,
): number | undefined => {
  const values = getTargetValuesByBuildingType(target, referenceValues);
  if (!values) return undefined;

  const weighted = Object.entries(activityMix).reduce<{
    value: number;
    area: number;
  }>(
    (totals, [activity, area]) => {
      const buildingType =
        TARGET_BUILDING_TYPE_BY_ACTIVITY[activity as ActivityType];
      if (!buildingType || !area || area <= 0) return totals;
      return {
        value: totals.value + values[buildingType] * area,
        area: totals.area + area,
      };
    },
    { value: 0, area: 0 },
  );

  return weighted.area > 0
    ? weighted.value / weighted.area
    : values[TargetBuildingType.Other];
};

/**
 * Whether a target grades exactly what the scope reports. A limit value written
 * for narrower modules than the scope shows (Miljöbyggnad Guld grades A1-A5
 * while its scope reports A-C) is not a target for the result on screen — it
 * measures something else — so it cannot stand in as the scope's own target.
 */
export const targetGradesScope = (
  target: ScopeTarget,
  scopeLifecycles: Lifecycle[],
): boolean => {
  const { lifecycles } = target;
  if (!lifecycles?.length) return true;
  return (
    lifecycles.length === scopeLifecycles.length &&
    lifecycles.every((lifecycle) => scopeLifecycles.includes(lifecycle))
  );
};
